import React, { useState } from 'react';
import { CRow, CCol, CCard, CCardHeader, CCardBody, CCardTitle, CFormGroup, CSelect } from '@coreui/react';
import _t from 'counterpart';
import InputHeaderNoButton from '../../components/InputHeaderNoButton';
import ButtonWithLoader from '../../components/ButtonWithLoader';
import { useMutation, useQuery } from 'react-query';
import { extractErrorMessage } from '../../helpers';
import { loadGroups, loadMt5Groups, updateMt5WalletConfig, updateWalletConfig } from '../../services/BackendService';
import toast from 'react-hot-toast';
import { IGroup, IMt5Group } from '../groups/types';
import { useGetSetting } from './hooks';

interface IParams {
  group: string;
}

const WalletConfiguration = () => {
  const [groupId, setGroupId] = useState<string>('');
  // MT5's default group is stored separately (DEFAULT_MT5_GROUP) and its options come from the MT5
  // server, so it gets its own setting, query and mutation rather than sharing the Wintrado ones.
  const [mt5Group, setMt5Group] = useState<string>('');

  const onError = (e: any) => {
    toast.error(extractErrorMessage(e));
  };

  useGetSetting('DEFAULT_GROUP', 'default-group', {
    retry: false,
    refetchOnWindowFocus: false,
    onSuccess: (value: string) => {
      setGroupId(value);
    },
    onError: (e: any) => {
      toast.error(extractErrorMessage(e));
    },
  });

  const walletSettingsMutation = useMutation(({ group }: IParams) => updateWalletConfig(group), {
    onError,
    onSuccess: () => {
      toast.success(_t('onboarding.wallet-settings-configured'));
    },
  });

  useGetSetting('DEFAULT_MT5_GROUP', 'default-mt5-group', {
    retry: false,
    refetchOnWindowFocus: false,
    onSuccess: (value: string) => {
      setMt5Group(value);
    },
    onError: (e: any) => {
      toast.error(extractErrorMessage(e));
    },
  });

  const mt5WalletSettingsMutation = useMutation(({ group }: IParams) => updateMt5WalletConfig(group), {
    onError,
    onSuccess: () => {
      toast.success(_t('onboarding.wallet-settings-configured'));
    },
  });

  // MT5 may be off for this deployment, in which case the endpoint 503s -- treated as "no options"
  // rather than surfaced as an error, so the Wintrado half of this card still works.
  const loadMt5GroupsQuery = useQuery(['config-mt5-groups'], () => loadMt5Groups(), {
    retry: false,
    refetchOnWindowFocus: false,
  });

  const loadGroupsQuery = useQuery(['config-order-groups'], () => loadGroups(), {
    onError,
    onSuccess: (data: Array<IGroup>) => {
      if (data.length > 0 && groupId === '') {
        setGroupId(data[0].name);
      }
    },
  });

  const callMutation = () => {
    walletSettingsMutation.mutate({ group: groupId });
  };

  const onGroupChanged = (e: any) => {
    const value = e.target.value;
    setGroupId(value);
  };

  const callMt5Mutation = () => {
    mt5WalletSettingsMutation.mutate({ group: mt5Group });
  };

  const mt5Groups: Array<IMt5Group> = loadMt5GroupsQuery.data ?? [];

  return (
    <CCol>
      <CCard>
        <CCardHeader>
          <CCardTitle>{_t('onboarding.wallet-configuration')}</CCardTitle>
          <p className="mb-0 mt-1 small">{_t('onboarding.wallet-config-description')}</p>
        </CCardHeader>
        <CCardBody className="simulated-overflow-y">
          <CRow>
            <CCol md={12}>
              <CFormGroup>
                <InputHeaderNoButton labelText={_t('onboarding.group')} />
                <CSelect id="nf-type" name="nf-type" onChange={onGroupChanged} value={groupId}>
                  {loadGroupsQuery.data?.map((o: any) => (
                    <option value={o.name} key={`group-${o.id}`}>
                      {o.name}
                    </option>
                  ))}
                </CSelect>
              </CFormGroup>
            </CCol>
          </CRow>
          <CRow>
            <CCol md={12}>
              <ButtonWithLoader
                isLoading={walletSettingsMutation.isLoading}
                onClick={callMutation}
                buttonColor="primary"
                spinnerColor="secondary"
                title={_t('action.save')}
                className="btn-md"
                showText={false}
              />
            </CCol>
          </CRow>
          {mt5Groups.length > 0 && (
            <>
              <CRow>
                <CCol md={12}>
                  <CFormGroup>
                    <InputHeaderNoButton labelText={_t('onboarding.mt5-group')} />
                    <CSelect
                      id="nf-mt5-group"
                      name="nf-mt5-group"
                      onChange={(e: any) => setMt5Group(e.target.value)}
                      value={mt5Group}
                    >
                      <option value="">{_t('onboarding.mt5-group-none')}</option>
                      {mt5Groups.map((g) => (
                        <option value={g.group} key={g.group}>
                          {g.group} ({g.currency})
                        </option>
                      ))}
                    </CSelect>
                  </CFormGroup>
                </CCol>
              </CRow>
              <CRow>
                <CCol md={12}>
                  <ButtonWithLoader
                    isLoading={mt5WalletSettingsMutation.isLoading}
                    onClick={callMt5Mutation}
                    buttonColor="primary"
                    spinnerColor="secondary"
                    title={_t('action.save')}
                    className="btn-md"
                    showText={false}
                  />
                </CCol>
              </CRow>
            </>
          )}
        </CCardBody>
      </CCard>
    </CCol>
  );
};

export default WalletConfiguration;
